import React from 'react';
import { ChevronBack, ChevronForward } from '@pwskills/rachnaui/Icons';
import { Typography } from '@pwskills/rachnaui';
import { IcertificateDetails } from '../../TypesAndInterfaces';
import { sendCertificateThumbnailClickedEvent } from '../utils/certificateGAEvents';

interface CertificateCarouselProps {
  certificates: IcertificateDetails[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  children: React.ReactNode;
}

const CertificateCarousel: React.FC<CertificateCarouselProps> = ({
  certificates,
  selectedIndex,
  onSelect,
  children,
}) => {
  const hasMultipleCertificates = certificates.length > 1;
  const partnerCertCount = certificates.filter((c) => c.isPartnerCertificate).length;

  const isFirstCertificate = selectedIndex === 0;
  const isLastCertificate = selectedIndex === certificates.length - 1;

  const handleNavigation = (direction: 'prev' | 'next') => {
    let newIndex: number;
    if (direction === 'prev') {
      if (isFirstCertificate) return;
      newIndex = selectedIndex - 1;
    } else {
      if (isLastCertificate) return;
      newIndex = selectedIndex + 1;
    }

    const cert = certificates[newIndex];
    sendCertificateThumbnailClickedEvent({
      certificate_id: cert.certificate?.id,
      certificate_index: newIndex,
      certificate_type: cert.certificate?.type || cert.certificateType,
      course_name: cert.courseName,
      course_id: cert.certificate?._id,
      partner_certs_available_count: partnerCertCount,
      is_partner_certificate: cert.isPartnerCertificate,
      partner_name: cert.partnerName,
    });

    onSelect(newIndex);
  };

  const handleDotClick = (index: number) => {
    if (index === selectedIndex) return;

    const cert = certificates[index];
    sendCertificateThumbnailClickedEvent({
      certificate_id: cert.certificate?.id,
      certificate_index: index,
      certificate_type: cert.certificate?.type || cert.certificateType,
      course_name: cert.courseName,
      course_id: cert.certificate?._id,
      partner_certs_available_count: partnerCertCount,
      is_partner_certificate: cert.isPartnerCertificate,
      partner_name: cert.partnerName,
    });

    onSelect(index);
  };

  return (
    <div className="flex flex-col w-full items-start gap-4">
      {hasMultipleCertificates && (
        <Typography className="text-gray-850" component="body-regular">
          Showing
          <span className="ml-2 text-[16px] text-gray-850 !font-bold">
            {selectedIndex + 1} of {certificates.length} certificates
          </span>
        </Typography>
      )}
      <div className="flex items-center w-full gap-2 medium:!gap-4">
        {hasMultipleCertificates && (
          <button
            type="button"
            onClick={() => handleNavigation('prev')}
            disabled={isFirstCertificate}
            className={`p-2 shadow-md rounded-full transition-colors flex-shrink-0 ${
              isFirstCertificate ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'
            }`}
            aria-label="Previous certificate"
          >
            <ChevronBack
              className={`w-5 h-5 medium:!w-6 medium:!h-6 ${
                isFirstCertificate ? 'text-gray-300' : 'text-orange-500'
              }`}
            />
          </button>
        )}

        <div className="flex-1">{children}</div>

        {hasMultipleCertificates && (
          <button
            type="button"
            onClick={() => handleNavigation('next')}
            disabled={isLastCertificate}
            className={`p-2 shadow-md rounded-full transition-colors flex-shrink-0 ${
              isLastCertificate ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'
            }`}
            aria-label="Next certificate"
          >
            <ChevronForward
              className={`w-5 h-5 medium:!w-6 medium:!h-6 ${
                isLastCertificate ? 'text-gray-300' : 'text-orange-500'
              }`}
            />
          </button>
        )}
      </div>

      {hasMultipleCertificates && (
        <div className="flex w-full gap-2 items-center justify-center mt-4 mb-4">
          {certificates.map((cert, index) => (
            <button
              key={`dot-${cert.certificate?.id || index}`}
              type="button"
              onClick={() => handleDotClick(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === selectedIndex ? 'bg-orange-500' : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to certificate ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CertificateCarousel;
